import { useState, useEffect, useMemo } from "react";
import {
  AdminAPICategory,
  Badge,
  Button,
  ButtonVariant,
  Text,
  TextField,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import { ExternalLink } from "lucide-react";
import EmptyStateWrapper from "../../shared/layout/EmptyStateWrapper";
import NotFoundBanner from "./NotFoundBanner";
import SyncPlanVisualization from "./SyncPlanVisualization";
import { LAState, LookupMode, ResolutionTier, SyncPlanConfig, SyncPlanConfigBase } from "./types";
import {
  ALL_CATEGORIES,
  fetchFullSyncPlanConfig,
  fetchJSON,
  getCategoryDisplayName,
  getSyncPlanConfigAdminUrl,
  LOOKUP_MODE_LABELS,
} from "./utils";

const SyncPlanVisualizationTab = () => {
  const [lookupMode, setLookupMode] = useState<LookupMode>("linked_account");
  const [inputId, setInputId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<AdminAPICategory | "">("");

  const [syncPlanConfigs, setSyncPlanConfigs] = useState<SyncPlanConfig[] | null>(null);
  const [directSyncPlanConfig, setDirectSyncPlanConfig] = useState<SyncPlanConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [laState, setLaState] = useState<LAState>({ status: "idle" });

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isValidUUID = UUID_RE.test(inputId.trim());

  const handleModeChange = (mode: LookupMode) => {
    setLookupMode(mode);
    setSelectedCategory("");
    setLaState({ status: "idle" });
    setSyncPlanConfigs(null);
    setDirectSyncPlanConfig(null);
    setError(null);
    setIsLoading(false);
  };

  // Linked account lookup
  useEffect(() => {
    if (lookupMode !== "linked_account") return;
    const id = inputId.trim();
    if (!id || !UUID_RE.test(id)) {
      setLaState({ status: "idle" });
      return;
    }
    setLaState({ status: "loading" });
    fetchJSON<{
      id: string;
      integration: { id: string; name: string };
      organization: { id: string };
      category: string;
      end_user: { organization_name: string | null } | null;
    }>(`integrations/linked-accounts/all/${id}`)
      .then((la) => {
        setLaState({
          status: "found",
          la: {
            id,
            integrationId: la.integration.id,
            integrationName: la.integration.name,
            organizationId: la.organization.id,
            category: la.category as AdminAPICategory,
            endUserOrganizationName: la.end_user?.organization_name ?? null,
          },
        });
      })
      .catch((err: unknown) => {
        const res = err as Response | undefined;
        if (res?.status === 403) {
          // Degraded (local dev: is_merge_org not set) — tier 1 + 3 only, no org/category info
          setLaState({
            status: "found",
            la: {
              id,
              integrationId: "",
              integrationName: "",
              organizationId: "",
              category: "" as AdminAPICategory,
              endUserOrganizationName: null,
            },
          });
        } else {
          setLaState({ status: "error", message: "Linked account not found." });
        }
      });
  }, [inputId, lookupMode]);

  const la = laState.status === "found" ? laState.la : null;

  // Configs fetch for linked_account and integration modes
  useEffect(() => {
    if (lookupMode === "sync_plan_config") return;

    let integrationId: string | null = null;
    let category: string | null = null;

    if (lookupMode === "linked_account") {
      integrationId = la?.integrationId ?? null;
      category = la?.category ?? null;
    } else {
      const id = inputId.trim();
      if (id && UUID_RE.test(id) && selectedCategory) {
        integrationId = id;
        category = selectedCategory;
      }
    }

    if (!integrationId || !category) {
      setSyncPlanConfigs(null);
      return;
    }

    setIsLoading(true);
    setSyncPlanConfigs(null);
    setError(null);

    fetchJSON<SyncPlanConfigBase[]>(
      `integrations/sync-plan-configurations?integration_id=${integrationId}&category=${category}`
    )
      .then((baseSyncPlanConfigs) => Promise.all(baseSyncPlanConfigs.map(fetchFullSyncPlanConfig)))
      .then((fullSyncPlanConfigs) => {
        setSyncPlanConfigs(fullSyncPlanConfigs);
        setIsLoading(false);
      })
      .catch(() => {
        setError("Failed to load sync plan configuration.");
        setIsLoading(false);
      });
  }, [lookupMode, la?.integrationId, la?.category, inputId, selectedCategory]);

  // Direct config fetch for sync_plan_config mode
  useEffect(() => {
    if (lookupMode !== "sync_plan_config") return;
    const id = inputId.trim();
    if (!id || !UUID_RE.test(id)) {
      setDirectSyncPlanConfig(null);
      return;
    }
    setIsLoading(true);
    setDirectSyncPlanConfig(null);
    setError(null);

    fetchJSON<SyncPlanConfigBase>(`integrations/sync-plan-configurations/${id}`)
      .then(fetchFullSyncPlanConfig)
      .then((fullSyncPlanConfig) => {
        setDirectSyncPlanConfig(fullSyncPlanConfig);
        setIsLoading(false);
      })
      .catch(() => {
        setError("Failed to load sync plan configuration.");
        setIsLoading(false);
      });
  }, [inputId, lookupMode]);

  const resolvedSyncPlanConfig = useMemo((): {
    config: SyncPlanConfig | null;
    tier: ResolutionTier;
  } | null => {
    if (lookupMode !== "linked_account" || !syncPlanConfigs || !la) return null;
    const linked = syncPlanConfigs.find((c) => c.active && c.linked_account === la.id);
    if (linked) return { config: linked, tier: "linked_account" };
    if (la.organizationId) {
      const org = syncPlanConfigs.find(
        (c) => c.active && c.organization === la.organizationId && !c.linked_account
      );
      if (org) return { config: org, tier: "organization" };
    }
    const integration = syncPlanConfigs.find(
      (c) => c.active && !c.organization && !c.linked_account
    );
    if (integration) return { config: integration, tier: "integration" };
    return { config: null, tier: "none" };
  }, [lookupMode, syncPlanConfigs, la]);

  const integrationDefaultSyncPlanConfig = useMemo(() => {
    if (lookupMode !== "integration" || !syncPlanConfigs) return null;
    return syncPlanConfigs.find((c) => c.active && !c.organization && !c.linked_account) ?? null;
  }, [lookupMode, syncPlanConfigs]);

  const tierLabel: Record<string, string> = {
    linked_account: "Linked Account Override",
    organization: "Org Override",
    integration: "Integration Default",
  };
  const tierColor: Record<string, "blue" | "orange" | "gray"> = {
    linked_account: "blue",
    organization: "orange",
    integration: "gray",
  };

  const isIdle =
    (lookupMode === "linked_account" && laState.status === "idle") ||
    (lookupMode === "sync_plan_config" && !inputId) ||
    (lookupMode === "integration" && (!inputId || !isValidUUID || !selectedCategory));

  const isLookingUp = lookupMode === "linked_account" && laState.status === "loading";

  const isReady =
    (lookupMode === "linked_account" && laState.status === "found") ||
    (lookupMode === "sync_plan_config" && isValidUUID) ||
    (lookupMode === "integration" && isValidUUID && !!selectedCategory);

  return (
    <div className="pb-8 px-8 pt-16">
      <Text variant="h2" className="mt-10">
        Sync plans
      </Text>
      <div className="my-6 bg-gray-10 h-[0.5px]" />
      <div className="flex gap-8 items-start">
        <div className="flex-1 min-w-0">
          {/* Search bar */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="w-[260px]">
              <Typeahead
                className="bg-white"
                options={Object.keys(LOOKUP_MODE_LABELS) as LookupMode[]}
                disableClearable
                onChange={(_: any, value: LookupMode | null) => {
                  if (value) handleModeChange(value);
                }}
                getOptionLabel={(mode: LookupMode) => LOOKUP_MODE_LABELS[mode]}
                renderOption={(mode: LookupMode) => <Text>{LOOKUP_MODE_LABELS[mode]}</Text>}
                value={lookupMode}
              />
            </div>
            <TextField
              className="bg-white w-80"
              type="search"
              value={inputId}
              onChange={(e) => setInputId(e.target.value)}
              placeholder="paste a UUID"
            />
            {lookupMode === "integration" && isValidUUID && (
              <div className="w-[200px]">
                <Typeahead
                  className="bg-white"
                  options={ALL_CATEGORIES}
                  onChange={(_: any, value: AdminAPICategory | null) =>
                    setSelectedCategory(value ?? "")
                  }
                  getOptionLabel={(cat: AdminAPICategory) => getCategoryDisplayName(cat)}
                  renderOption={(cat: AdminAPICategory) => (
                    <Text>{getCategoryDisplayName(cat)}</Text>
                  )}
                  value={selectedCategory || null}
                  placeholder="Select category…"
                />
              </div>
            )}
            {inputId && !isValidUUID && <span className="text-xs text-red-500">Invalid UUID</span>}
            {isLookingUp && <span className="text-xs text-gray-400">Looking up…</span>}
          </div>

          {/* LA summary row */}
          {lookupMode === "linked_account" &&
            la &&
            (la.endUserOrganizationName || la.integrationName || la.category) && (
              <div className="flex items-center gap-2 mb-6">
                {la.endUserOrganizationName && (
                  <Badge color="gray">End User: {la.endUserOrganizationName}</Badge>
                )}
                {la.integrationName && (
                  <Badge color="gray">Integration: {la.integrationName}</Badge>
                )}
                {la.category && (
                  <Badge color="gray">Category: {getCategoryDisplayName(la.category)}</Badge>
                )}
              </div>
            )}

          {isIdle && (
            <Text variant="md" className="text-gray-500 py-16 text-center block">
              {lookupMode === "linked_account" &&
                "Enter a Linked Account ID to view the applicable sync plan configuration."}
              {lookupMode === "sync_plan_config" &&
                "Enter a Sync Plan Configuration ID to view it directly."}
              {lookupMode === "integration" &&
                (!inputId || !isValidUUID
                  ? "Enter an Integration ID to view its sync plan configuration."
                  : "Select a category to load the sync plan configuration.")}
            </Text>
          )}

          {lookupMode === "linked_account" && laState.status === "error" && (
            <NotFoundBanner label="Linked Account" uuid={inputId.trim()} />
          )}

          {(isLookingUp || (isReady && isLoading)) && <EmptyStateWrapper isSpinner />}

          {isReady && error && !isLoading && (
            <NotFoundBanner label={LOOKUP_MODE_LABELS[lookupMode]} uuid={inputId.trim()} />
          )}

          {/* Linked account result */}
          {lookupMode === "linked_account" &&
            laState.status === "found" &&
            !isLoading &&
            !error &&
            (() => {
              if (
                !resolvedSyncPlanConfig ||
                resolvedSyncPlanConfig.tier === "none" ||
                !resolvedSyncPlanConfig.config
              ) {
                return (
                  <Text variant="md" className="text-gray-500 py-16 text-center block">
                    No active sync plan configuration found for this linked account.
                  </Text>
                );
              }
              return (
                <>
                  <div className="border-t border-gray-10 mt-2 mb-8" />
                  <div className="flex items-center gap-3 mb-4">
                    <Text variant="h5">Sync Plan Configuration</Text>
                    <Button
                      size="sm"
                      variant={ButtonVariant.SecondaryGray}
                      rightIcon={<ExternalLink size={14} />}
                      onClick={() =>
                        window.open(
                          getSyncPlanConfigAdminUrl(resolvedSyncPlanConfig.config!.id),
                          "_blank"
                        )
                      }
                    >
                      View in CMS
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge color={tierColor[resolvedSyncPlanConfig.tier]}>
                      {tierLabel[resolvedSyncPlanConfig.tier]}
                    </Badge>
                    <Text variant="caption" className="text-gray-700">
                      {resolvedSyncPlanConfig.tier === "linked_account" &&
                        "matched on linked account ID"}
                      {resolvedSyncPlanConfig.tier === "organization" &&
                        "no linked account override — matched on org"}
                      {resolvedSyncPlanConfig.tier === "integration" &&
                        "no linked account or org override — using integration default"}
                    </Text>
                  </div>
                  <SyncPlanVisualization
                    config={resolvedSyncPlanConfig.config}
                    integrationID={la!.integrationId}
                  />
                </>
              );
            })()}

          {/* Sync plan config ID result */}
          {lookupMode === "sync_plan_config" && !isLoading && !error && directSyncPlanConfig && (
            <>
              <div className="border-t border-gray-10 mt-2 mb-8" />
              <div className="flex items-center gap-3 mb-4">
                <Text variant="h5">Sync Plan Configuration</Text>
                <Button
                  size="sm"
                  variant={ButtonVariant.SecondaryGray}
                  rightIcon={<ExternalLink size={14} />}
                  onClick={() =>
                    window.open(getSyncPlanConfigAdminUrl(directSyncPlanConfig.id), "_blank")
                  }
                >
                  View in CMS
                </Button>
              </div>
              <SyncPlanVisualization
                config={directSyncPlanConfig}
                integrationID={directSyncPlanConfig.integration}
              />
            </>
          )}

          {/* Integration ID result */}
          {lookupMode === "integration" &&
            !isLoading &&
            !error &&
            syncPlanConfigs !== null &&
            (() => {
              if (!integrationDefaultSyncPlanConfig) {
                return (
                  <Text variant="md" className="text-gray-500 py-16 text-center block">
                    No active integration-level sync plan configuration found for this integration
                    and category.
                  </Text>
                );
              }
              return (
                <>
                  <div className="border-t border-gray-10 mt-2 mb-8" />
                  <div className="flex items-center gap-3 mb-4">
                    <Text variant="h5">Sync Plan Configuration</Text>
                    <Button
                      size="sm"
                      variant={ButtonVariant.SecondaryGray}
                      rightIcon={<ExternalLink size={14} />}
                      onClick={() =>
                        window.open(
                          getSyncPlanConfigAdminUrl(integrationDefaultSyncPlanConfig.id),
                          "_blank"
                        )
                      }
                    >
                      View in CMS
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge color="gray">Integration Default</Badge>
                  </div>
                  <SyncPlanVisualization
                    config={integrationDefaultSyncPlanConfig}
                    integrationID={inputId.trim()}
                  />
                </>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export default SyncPlanVisualizationTab;
